import type {
  EscalationContacts,
  EscalationContactsWire,
} from './escalationContacts.types'
import { api } from './index'

// Pure wire→domain mapper, exported for direct unit testing (mirrors
// recoveryAi's transformConversation). Extracts ids only and is nil-safe:
// null primary → null, missing/absent additional → []. The full user objects
// are resolved separately via merchantUsersApi.getMerchantUsers.
//
// A DANGLING contact — a stored row whose merchant_user no longer resolves —
// arrives as a null ELEMENT, which `?? []` does not cover: that guards an absent
// key, not a null member. Dropping it here is what stops one orphaned row
// throwing, rejecting the whole GET, and reading to the Finalize gate as
// "recipients unavailable" (which blocks activation) rather than as one lost
// recipient. Orphaning takes one click: deleting a merchant user destroys the
// membership link and leaves the escalation-contact row behind.
export const transformEscalationContacts = (
  response: EscalationContactsWire
): EscalationContacts => {
  const contacts = response?.escalation_contacts
  return {
    primaryUserId: contacts?.primary?.id ?? null,
    additionalUserIds: (contacts?.additional ?? [])
      .map(user => user?.id)
      .filter((id): id is number => id != null),
  }
}

// FE consumer of the live BE-5 `recovery/escalation_contacts` resource (raw-int
// merchant_user ids). GET returns the saved set (or an empty set when unset);
// PUT replaces it (an empty body clears it server-side).
export const escalationContactsApi = api.injectEndpoints({
  endpoints: builder => ({
    getEscalationContacts: builder.query<EscalationContacts, void>({
      query: () => 'recovery/escalation_contacts',
      transformResponse: transformEscalationContacts,
      providesTags: ['EscalationContacts'],
    }),
    setEscalationContacts: builder.mutation<
      EscalationContacts,
      EscalationContacts
    >({
      query: ({ primaryUserId, additionalUserIds }) => ({
        url: 'recovery/escalation_contacts',
        method: 'PUT',
        body: {
          primary_merchant_user_id: primaryUserId,
          additional_merchant_user_ids: additionalUserIds,
        },
      }),
      transformResponse: transformEscalationContacts,
      // Read-your-writes: the PUT returns the authoritative saved set, so write
      // it into the getEscalationContacts cache the instant it resolves. The
      // wizard's Finalize gate reads that cache, so without this the gate stays
      // on the pre-save value until a refetch lands — which, on a lagging read
      // replica, can even return the OLD set.
      // Patch on FULFILMENT, never on start: an optimistic patch would open the
      // activation gate before the server confirmed a reachable human.
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data: updated } = await queryFulfilled
          dispatch(
            escalationContactsApi.util.updateQueryData(
              'getEscalationContacts',
              undefined,
              () => updated
            )
          )
        } catch {
          // Fail soft: leave the cache; the invalidation refetch still corrects it.
        }
      },
      // Invalidate on FAILURE ONLY. On success the patch above has already written
      // the authoritative set to the one cache entry anyone reads, so a refetch
      // adds nothing except a window in which a lagging read replica serves the
      // pre-write set — and the Finalize gate would go grey right after a save
      // that landed. On failure the cache was deliberately left alone, so the
      // refetch is the only thing that reconciles it.
      invalidatesTags: (_result, error) =>
        error ? ['EscalationContacts'] : [],
    }),
  }),
})

export const {
  useGetEscalationContactsQuery,
  useSetEscalationContactsMutation,
} = escalationContactsApi
